import { useState } from "react";
import { RefreshCw, CheckCircle2 } from "lucide-react";
import { useLifeState } from "@/context/LifeStateContext";
import { usePeriod } from "@/context/PeriodContext";
import { reviewMeetings, periodKey, stripHtml } from "@/lib/life";
import { periodLabel } from "@/lib/wellness";
import { PageHeader, SectionCard, ProgressBar, AddButton } from "@/components/shared";
import { PeriodNav } from "@/components/PeriodNav";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export default function Reviews() {
  const { state, update, loading } = useLifeState();
  const { horizon, setHorizon, date, setDate } = usePeriod();
  const [expanded, setExpanded] = useState(null);

  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const meeting = reviewMeetings[horizon] || reviewMeetings.Week;
  const key = horizon + ":" + periodKey(horizon, date);
  const review = state.reviews?.[key] || { answers: {} };
  const answers = review.answers || {};
  const answered = meeting.prompts.filter((p) => stripHtml(answers[p.id])).length;

  const setAnswer = (pid, val) => update((prev) => {
    const reviews = { ...(prev.reviews || {}) };
    const cur = reviews[key] || { answers: {} };
    reviews[key] = { ...cur, answers: { ...cur.answers, [pid]: val } };
    return { ...prev, reviews };
  }, `Updated ${horizon} review`);

  const complete = () => { update((prev) => {
    const reviews = { ...(prev.reviews || {}) };
    const cur = reviews[key] || { answers: {} };
    reviews[key] = { ...cur, completedAt: new Date().toISOString() };
    return { ...prev, reviews };
  }, `Completed ${horizon} review`); toast.success("Review marked complete"); };

  return (
    <div>
      <PageHeader title="Reviews" subtitle="Learn and adjust with honesty and grace — never putting your worth on trial." testid="life-reviews-header" />

      <div className="mb-4"><PeriodNav horizon={horizon} setHorizon={setHorizon} date={date} setDate={setDate} testid="life-review-period" /></div>

      <SectionCard className="mb-4" testid="review-purpose-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <span className="grid place-items-center h-10 w-10 rounded-[10px] bg-[color:rgba(31,115,85,0.12)] text-[var(--green)] shrink-0"><RefreshCw className="h-5 w-5" /></span>
            <div><h3 className="font-serif-display text-xl text-[var(--text)]">{meeting.title} · <span className="text-[var(--text-muted)]">{periodLabel(horizon, date)}</span></h3><p className="text-sm text-[var(--text-muted)] mt-1 max-w-2xl">{meeting.purpose}</p></div>
          </div>
          <div className="flex items-center gap-3">
            {review.completedAt && <span className="inline-flex items-center gap-1 text-[11px] text-[var(--green)]"><CheckCircle2 className="h-3.5 w-3.5" />Completed</span>}
            <div className="min-w-[140px]">
              <div className="flex items-center justify-between text-xs text-[var(--text-muted)] mb-1"><span>Reflected</span><span className="tabular-nums">{answered}/{meeting.prompts.length}</span></div>
              <ProgressBar value={answered} max={meeting.prompts.length} tone="green" />
            </div>
          </div>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {meeting.prompts.map((p) => (
          <SectionCard key={p.id} title={p.title} subtitle={p.help} testid={`review-prompt-${p.id}`}>
            <RichTextEditor
              value={answers[p.id] || ""}
              onChange={(html) => setAnswer(p.id, html)}
              placeholder="Reflect honestly… bold what matters, list your takeaways"
              onExpand={() => setExpanded(p)}
              testid={`review-input-${p.id}`}
            />
          </SectionCard>
        ))}
      </div>

      <Dialog open={!!expanded} onOpenChange={(o) => !o && setExpanded(null)}>
        <DialogContent className="max-w-3xl" data-testid="review-expand-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-xl">{expanded?.title}</DialogTitle></DialogHeader>
          {expanded && (
            <div>
              <p className="text-sm text-[var(--text-muted)] mb-3">{expanded.help}</p>
              <RichTextEditor value={answers[expanded.id] || ""} onChange={(html) => setAnswer(expanded.id, html)} placeholder="Reflect freely here…" minHeight={340} testid="review-expand-editor" />
              <div className="flex justify-end mt-4">
                <Button onClick={() => setExpanded(null)} className="bg-[var(--green)] text-[#f7fbf9] hover:bg-[color:rgba(31,115,85,0.9)]" data-testid="review-expand-done">Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="mt-4 flex justify-end">
        <AddButton onClick={complete} testid="complete-review-button"><CheckCircle2 className="h-4 w-4 mr-1.5" />Mark review complete</AddButton>
      </div>
    </div>
  );
}
